import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dumbbell, Play, History, ChevronDown, ChevronUp, Check, Lock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
}

function formatTime(sec) {
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function HistoryItem({ log }) {
  const [open, setOpen] = useState(false);
  const total = log.exercises?.reduce((s, e) => s + e.sets.filter(x => x.completed).length, 0) || 0;
  const totalSets = log.exercises?.reduce((s, e) => s + e.sets.length, 0) || 0;

  return (
    <div className="bg-[#0f0f0f] border border-white/6 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/3 transition-colors"
      >
        <div className="flex items-center gap-3 text-left">
          <div className="w-8 h-8 rounded-lg bg-[#C9A84C]/10 border border-[#C9A84C]/20 flex items-center justify-center shrink-0">
            <Dumbbell size={13} className="text-[#C9A84C]" />
          </div>
          <div>
            <p className="text-white text-sm font-semibold">{log.training_naam || "Vrije training"}</p>
            <p className="text-zinc-500 text-xs">
              {formatDate(log.logged_at)} · {log.exercises?.length || 0} oefen. · {total}/{totalSets} sets · {formatTime(log.duration_seconds || 0)}
            </p>
          </div>
        </div>
        {open ? <ChevronUp size={14} className="text-zinc-500 shrink-0" /> : <ChevronDown size={14} className="text-zinc-500 shrink-0" />}
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-white/6 space-y-3 mt-3">
          {log.exercises?.map((ex, i) => (
            <div key={i}>
              <p className="text-white text-xs font-bold mb-1.5">{ex.naam}</p>
              <div className="flex flex-wrap gap-1.5">
                {ex.sets.map((s, j) => (
                  <span key={j} className={`px-2 py-0.5 rounded-lg text-xs border flex items-center gap-1 ${s.completed ? "border-[#C9A84C]/30 text-[#C9A84C] bg-[#C9A84C]/8" : "border-white/8 text-zinc-500"}`}>
                    {s.completed && <Check size={9} />}
                    {s.kg ? `${s.kg}kg × ${s.reps}` : `× ${s.reps || "–"}`}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function VrijeTrainingSection({ user }) {
  const navigate = useNavigate();
  const token = user?.token;
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vrijeTrainingEnabled, setVrijeTrainingEnabled] = useState(true);
  const [checkDone, setCheckDone] = useState(false);

  // Haal toegangsstatus op (werkt voor zowel directe users als bedrijfssporters)
  useEffect(() => {
    if (!token) { setCheckDone(true); return; }
    fetch(`${API}/api/me/vrije-training-access`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) setVrijeTrainingEnabled(d.enabled ?? true);
      })
      .catch(() => {})
      .finally(() => setCheckDone(true));
  }, [token]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/workout/vrije_training/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      setHistory(d.logs || []);
    } catch {}
    setLoading(false);
  };

  const toggleHistory = () => {
    if (!showHistory) loadHistory();
    setShowHistory(!showHistory);
  };

  if (!checkDone) return null;

  // Vergrendeld scherm als vrije training uitgeschakeld is
  if (!vrijeTrainingEnabled) {
    return (
      <div className="bg-[#0f0f0f] border border-white/8 rounded-2xl p-5" data-testid="vrije-training-locked">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-zinc-800 border border-white/8 flex items-center justify-center">
            <Lock size={16} className="text-zinc-500" />
          </div>
          <div>
            <p className="text-zinc-400 font-bold">Vrije Training</p>
            <p className="text-zinc-600 text-xs">Niet beschikbaar</p>
          </div>
        </div>
        <p className="text-zinc-500 text-sm">
          Vrije Training is uitgeschakeld door je coach. Neem contact op als je hier gebruik van wilt maken.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-[#0f0f0f] border border-white/8 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#C9A84C]/10 border border-[#C9A84C]/20 flex items-center justify-center">
            <Dumbbell size={18} className="text-[#C9A84C]" />
          </div>
          <div>
            <p className="text-white font-bold">Vrije Training</p>
            <p className="text-zinc-500 text-xs">Maak je eigen training</p>
          </div>
        </div>
        <button
          onClick={toggleHistory}
          data-testid="vrije-training-history-toggle"
          className="text-zinc-500 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/5"
          title="Geschiedenis"
        >
          <History size={16} />
        </button>
      </div>

      <button
        onClick={() => navigate("/vrije-training")}
        data-testid="vrije-training-start-btn"
        className="w-full py-3 rounded-xl bg-[#C9A84C]/15 border border-[#C9A84C]/30 text-[#C9A84C] font-bold text-sm hover:bg-[#C9A84C]/25 transition-all flex items-center justify-center gap-2"
      >
        <Play size={15} /> Training starten
      </button>

      {showHistory && (
        <div className="mt-4 border-t border-white/6 pt-4">
          <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest mb-3">Geschiedenis</p>
          {loading ? (
            <p className="text-zinc-600 text-sm text-center py-4">Laden...</p>
          ) : history.length === 0 ? (
            <p className="text-zinc-600 text-sm text-center py-4">Nog geen vrije trainingen opgeslagen.</p>
          ) : (
            <div className="space-y-2">
              {history.map((log, i) => <HistoryItem key={i} log={log} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
